/**
 * Suggestion chips for the practice Home. The backend's generated chips
 * (`source: "llm"`) are written from the last week's activity and may name any
 * client or case, so they drift into case prep ("Review the cover letter",
 * "Check the I-485 forms"). That work belongs in the case IDE, so Home drops
 * them and offers a curated list of questions a practice manager asks across
 * the whole firm: what is urgent, how many cases are open, what is due, what
 * expires, who is carrying what. The rule chips (real alerts) are kept out of
 * the generated set the same way the client view does and stay first when the
 * caller asks for them (`includeAlerts`).
 *
 * Every entry is answerable with the practice-focus read tools:
 * `firm_overview` (counts by status and visa type, unassigned), `list_cases`
 * (status, deadline, RFE and notice flags, assignees), `upcoming_deadlines`
 * (filing deadlines, client expirations, reminders and appointments, overdue
 * included), `team_workload` (open cases per person, unassigned cases, overdue
 * reminders per person) and `recent_activity` (conversations and client
 * records that changed).
 */
import type { PracticeDashboard, Recommendation } from "./types";

/** How many chips the row shows, alert chips included. */
export const PRACTICE_CHIP_COUNT = 4;
/** The curated chips always get at least this many of those slots. */
const MIN_CURATED = 2;
/** The status the cases tool filters on for "On Hold". */
const ON_HOLD = "On Hold";

type PoolEntry = {
  id: string;
  title: string;
  prompt: string;
  reason: string;
};

const entry = (
  id: string,
  title: string,
  prompt: string,
  reason: string
): PoolEntry => ({ id, title, prompt, reason });

const URGENT = entry(
  "urgent",
  "Which Cases Need Urgent Attention?",
  "Which cases need urgent attention right now? Look at filing deadlines that are overdue or due in the next 7 days, open RFEs and other notices, overdue reminders and expired documents across the practice. List each case with the reason and who is assigned.",
  "Overdue and near-term deadlines, notices and expired documents."
);
const OPEN = entry(
  "open",
  "How Many Cases Do We Have Open?",
  "How many cases do we have open? Give the total and break it down by status and visa type, and tell me how many have no one assigned.",
  "A count of open cases by status and visa type."
);
const FILE_BY_FRIDAY = entry(
  "file-this-week",
  "Which Cases Need to Be Filed by End of Week?",
  "Which cases need to be filed by the end of this week? List each one with its filing deadline, status and who is assigned, and flag any that are already past due.",
  "Filing deadlines inside the week."
);
const EXPIRATIONS = entry(
  "expirations",
  "Which Clients Have Sensitive Expirations in the Next 3 Months?",
  "Which clients have sensitive expirations in the next 3 months? List the passports, visas, status end dates, work permits and I-94s that expire in the next 90 days, with the client, what expires and when, soonest first.",
  "Passports, visas and work permits coming due."
);
const DEADLINES_WEEK = entry(
  "deadlines-week",
  "What Deadlines Are Coming Up This Week?",
  "What deadlines are coming up this week? Include filing deadlines, document expirations, reminders and appointments across the practice, soonest first.",
  "Everything dated in the next 7 days."
);
const ON_HOLD_CASES = entry(
  "on-hold",
  "Which Cases Are On Hold?",
  "Which cases are on hold? List each one with its client, visa type, filing deadline and who is assigned, so I can decide which to restart.",
  "Cases parked with the On Hold status."
);
const OVERDUE_REMINDERS = entry(
  "overdue-reminders",
  "Which Clients Have Overdue Reminders?",
  "Which clients have overdue reminders? List each reminder with the client, how many days late it is and who it is assigned to.",
  "Reminders past their due date."
);
const WORKLOAD = entry(
  "workload",
  "Who Has the Heaviest Workload on the Team?",
  "Who has the heaviest workload on the team? Show open cases per person with their next deadline and overdue reminders, and say who has room to take more.",
  "Open cases and overdue reminders per person."
);
const CHANGED = entry(
  "changed",
  "What Changed Across Our Cases Since Yesterday?",
  "What changed across our cases and clients since yesterday? Summarize the record activity and the conversations that moved, and call out anything I should act on.",
  "Activity on cases, clients and conversations."
);
const UNASSIGNED = entry(
  "unassigned",
  "Which Cases Have No One Assigned?",
  "Which cases have no one assigned? List them with their client, visa type and filing deadline, soonest deadline first.",
  "Cases nobody owns yet."
);
const NOTICES = entry(
  "notices",
  "Which Cases Are Responding to a Notice?",
  "Which cases are responding to an RFE, NOID or NOIR? List each one with the client, the notice, the response deadline and who is assigned.",
  "Open RFEs, NOIDs and NOIRs."
);

/** The curated order when nothing on Home says otherwise. */
const BASE_ORDER: readonly PoolEntry[] = [
  URGENT,
  OPEN,
  FILE_BY_FRIDAY,
  EXPIRATIONS,
  DEADLINES_WEEK,
  NOTICES,
  ON_HOLD_CASES,
  OVERDUE_REMINDERS,
  WORKLOAD,
  UNASSIGNED,
  CHANGED
];

const toRecommendation = (e: PoolEntry): Recommendation => ({
  id: `practice-starter:${e.id}`,
  source: "starter",
  title: e.title,
  reason: e.reason,
  severity: "info",
  action: { type: "prompt", prompt: e.prompt }
});

/**
 * The curated practice chips, in the order they are offered. What Home has
 * already loaded moves the relevant ones to the front (overdue deadlines lead
 * with urgent attention, expirations inside 30 days with expirations, filings
 * due this week, unassigned cases) and drops the ones that would come back
 * empty (no cases on hold, nobody unassigned, no overdue reminders, a team of
 * one). Without a summary the order is the stable base order.
 */
export const practiceSuggestionPool = (
  summary: PracticeDashboard | null | undefined
): Recommendation[] => {
  if (!summary) return BASE_ORDER.map(toRecommendation);
  const counts = summary.greeting_counts;
  const onHold = summary.cases?.by_status?.[ON_HOLD] ?? 0;
  const overdueReminders =
    (counts?.overdue_mine ?? 0) + (counts?.overdue_team ?? 0);

  const empty = new Set<PoolEntry>();
  if (summary.cases && onHold === 0) empty.add(ON_HOLD_CASES);
  if (counts && counts.unassigned_matters === 0) empty.add(UNASSIGNED);
  if (counts && overdueReminders === 0) empty.add(OVERDUE_REMINDERS);
  if (summary.team && summary.team.length < 2) empty.add(WORKLOAD);

  const lead: PoolEntry[] = [];
  if ((counts?.deadlines_overdue ?? 0) > 0) lead.push(URGENT);
  if ((counts?.expiring_soon ?? 0) > 0) lead.push(EXPIRATIONS);
  if ((counts?.deadlines_this_week ?? 0) > 0) lead.push(FILE_BY_FRIDAY);
  if ((counts?.unassigned_matters ?? 0) > 0) lead.push(UNASSIGNED);

  const rest = BASE_ORDER.filter((e) => !lead.includes(e));
  return [...lead, ...rest].filter((e) => !empty.has(e)).map(toRecommendation);
};

/** The backend's real alert chips: the only ones of its practice chips kept. */
export const practiceAlerts = (
  recommendations: readonly Recommendation[]
): Recommendation[] => recommendations.filter((r) => r.source === "rule");

/**
 * The chips to show: the alert chips first when asked for (never the
 * generated ones), then a window of the curated pool that fills the row.
 * `round` advances the window, so "New Suggestions" cycles the curated list
 * without asking the model.
 */
export const practiceChips = (
  recommendations: readonly Recommendation[],
  summary: PracticeDashboard | null | undefined,
  round: number,
  includeAlerts = true
): Recommendation[] => {
  const alerts = includeAlerts ? practiceAlerts(recommendations) : [];
  const pool = practiceSuggestionPool(summary);
  const slots = Math.min(
    pool.length,
    Math.max(MIN_CURATED, PRACTICE_CHIP_COUNT - alerts.length)
  );
  const start = pool.length ? (round * slots) % pool.length : 0;
  const curated = Array.from(
    { length: slots },
    (_, i) => pool[(start + i) % pool.length]
  );
  const seen = new Set(
    alerts.map((r) =>
      r.action.type === "prompt" ? r.action.prompt.toLowerCase() : r.id
    )
  );
  return [
    ...alerts,
    ...curated.filter(
      (c) =>
        c.action.type !== "prompt" || !seen.has(c.action.prompt.toLowerCase())
    )
  ];
};
